import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, styled, useMediaQuery, useTheme } from "@mui/system";
import PreviewIcon from "@mui/icons-material/Preview";
import { Button, Typography, IconButton } from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import CircularProgress from "@mui/material/CircularProgress";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BarChartIcon from "@mui/icons-material/BarChart";
import DashboardIcon from "@mui/icons-material/Dashboard";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CCarousel from "../../../basecomp/components/CCarousel";
import CButton from "../../../basecomp/components/CButton2";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";
import { AssetDetailsAPI } from "../viewmodel/AssetDetailsAPI";
import AssignModal from "./AssignModal";
import ReturnModal from "./ReturnModal";
import AssetUpdateModal from "./AssetUpdateModal";
import AckBanner from "./AckBanner";
import EditHistorySection from "./EditHistorySection";
import RetireModal, { RestoreModal } from "./RetireModal";
import RepairSection from "./RepairSection";
import PrintSizeModal from "./PrintSizeModal";
import { fetchSizeOptions } from "../model/AssetUpload";
import BlockIcon from "@mui/icons-material/Block";

const OrderBox = styled("div")({
  textAlign: "start",
  border: "1px solid #3CB043",
  width: "100%",
  margin: "1rem 0em",
  borderRadius: "1.2em 1.2em 1em 1em",
  overflow: "hidden",
});

const OrderBoxBtn = styled("div")({
  padding: "2em 3em",
  display: "flex",
  justifyContent: "center",
});

function AssetDetailsView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assetId = searchParams.get("id");
  const ackToken = searchParams.get("ack"); // from the "I have received this asset" email button
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const { handleToastOpen } = useToast();

  // State variables
  const [assetDetails, setAssetDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [openCarousel, setOpenCarousel] = useState(false);
  const [localValidToken, setLocalValidToken] = useState(false);
  const [assignPopState, setAssignPopState] = useState(false);
  const [statusPopState, setStatusPopState] = useState(false);
  const [userPermissions, setUserPermissions] = useState([]);
  const [hasLocalData, setHasLocalData] = useState(false);

  // Accordion state
  const [generalInfoExpanded, setGeneralInfoExpanded] = useState(true);
  const [assetInfoExpanded, setAssetInfoExpanded] = useState(true);
  const [historyExpanded, setHistoryExpanded] = useState(true);

  // Assignment history + return popup
  const [assetHistory, setAssetHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [returnPopState, setReturnPopState] = useState(false);
  const [retirePopState, setRetirePopState] = useState(false);
  const [restorePopState, setRestorePopState] = useState(false);
  // "Choose sticker size" popup (asset has no sticker size)
  const [printSizeAsk, setPrintSizeAsk] = useState(null);
  const [sizeOptions, setSizeOptions] = useState([]);

  const OrderBoxText = styled("div")({
    position: "relative", // allows absolute positioning inside
    backgroundColor: "#3CB043",
    color: "white",
    padding: ".5em",
    borderRadius: "0.7em 0.7em 0em 0em",
    fontSize: isXs ? ".9em" : "1.1em",
    display: "flex",
    alignItems: "center",
    justifyContent: "center", // centers the text baseline
    minHeight: "48px",
    boxSizing: "border-box",
  });

  useEffect(() => {
    if (assetId) {
      fetchAssetDetails();
    }
  }, [assetId]);

  useEffect(() => {
    if (localValidToken && assetId) {
      fetchAssetHistory();
    }
  }, [localValidToken, assetId]);

  const fetchAssetHistory = async () => {
    try {
      setHistoryLoading(true);
      const data = await AssetDetailsAPI.fetchAssetHistory(assetId);
      setAssetHistory(data);
    } catch (err) {
      console.error("Error fetching asset history:", err);
      setAssetHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // created_at / returned_at are seconds -> "dd/mm/yyyy, hh:mm am/pm"
  const formatDateTime = (value) => {
    if (!value || isNaN(Number(value))) return "-";
    return new Date(Number(value) * 1000).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const fetchAssetDetails = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(false); // Reset error state

      // Fetch asset details first
      const data = await AssetDetailsAPI.fetchAssetDetails(assetId);

      // Check if data exists
      if (!data) {
        setError(true);
        return; // Exit early if no data, skip permission checks
      }

      setAssetDetails(data);

      // Only proceed with permission/token checks if asset data is successfully fetched
      try {
        // Token check only if token exists
        const lc = AssetDetailsAPI.getLocalToken();
        const permissions = AssetDetailsAPI.getLocalPermission();

        // Check if both token and permissions exist in localStorage
        setHasLocalData(lc && permissions);

        if (lc && permissions) {
          const isValidToken = await AssetDetailsAPI.validateToken(lc);
          setLocalValidToken(isValidToken);

          // Get user permissions if token is valid
          if (isValidToken) {
            try {
              setUserPermissions(permissions);
            } catch (err) {
              console.error("Error getting permissions:", err);
              setUserPermissions([]);
            }
          }
        }
      } catch (permissionErr) {
        console.error("Error checking permissions:", permissionErr);
        // Don't set error to true for permission issues, just log them
        setLocalValidToken(false);
        setUserPermissions([]);
        setHasLocalData(false);
      }
    } catch (err) {
      console.error("Error fetching asset details:", err);
      setError(true); // Only set error for asset fetch failures
    } finally {
      setLoading(false);
    }
  };

  // Permission check functions
  const hasPermission = (permission) => {
    return localValidToken && userPermissions.includes(permission);
  };

  const canUploadImage = () => hasPermission("feature_asset_image");
  const canPrint = () => hasPermission("feature_asset_print");
  const canAssign = () => hasPermission("feature_asset_update");
  const canViewStatus = () => hasPermission("feature_asset_update");
  const canAccessDashboard = () => hasPermission("feature_asset_dashboard");

  // Function to get warning message and determine if buttons should be disabled
  const getWarningState = () => {
    // Case 1: No token or permissions in localStorage
    if (!hasLocalData) {
      return {
        showWarning: true,
        message:
          "You need to be logged in to use this feature. Click here to log in.",
        messageColor: "#6a4500",
        backgroundColor: "#ffc75e1e",
        isClickable: true,
        disableButtons: true,
      };
    }

    // Case 2: Token and permissions exist but token is invalid
    if (hasLocalData && !localValidToken) {
      return {
        showWarning: true,
        message:
          "You need to be logged in to use this feature. Click here to log in.",
        messageColor: "#6a4500",
        backgroundColor: "#ffc75e1e",
        isClickable: true,
        disableButtons: true,
      };
    }

    // Case 3: Token is valid but user doesn't have all permissions
    if (localValidToken && hasLocalData) {
      const allPermissions = [
        "feature_asset_image",
        "feature_asset_print",
        "feature_asset_update",
        "feature_asset_dashboard",
      ];
      const missingPermissions = allPermissions.filter(
        (perm) => !userPermissions.includes(perm)
      );

      if (missingPermissions.length === allPermissions.length) {
        // No permissions at all
        return {
          showWarning: true,
          message:
            "You don't have the permissions. Please Contact the Conas Administrator.",
          messageColor: "#6a4500",
          backgroundColor: "#ffc75e1e",
          isClickable: false,
          disableButtons: true,
        };
      } else if (missingPermissions.length > 0) {
        // Partial permissions
        const availableFeatures = [];
        if (userPermissions.includes("feature_asset_image"))
          availableFeatures.push("image feature");
        if (userPermissions.includes("feature_asset_print"))
          availableFeatures.push("print feature");
        if (userPermissions.includes("feature_asset_update"))
          availableFeatures.push("assign/status features");
        if (userPermissions.includes("feature_asset_dashboard"))
          availableFeatures.push("dashboard feature");

        return {
          showWarning: true,
          message: `You have ${availableFeatures.join(
            ", "
          )}. For additional features, please contact the Conas Administrator.`,
          messageColor: "#6a4500",
          backgroundColor: "#ffc75e1e",
          isClickable: false,
          disableButtons: false,
        };
      }
    }

    // Case 4: Token is valid and user has all permissions
    return {
      showWarning: false,
      disableButtons: false,
    };
  };

  // Button handlers
  const runPrint = async (sizeId, saveSize) => {
    try {
      await AssetDetailsAPI.generateAssetPdf(assetId, handleToastOpen, sizeId, saveSize);
      if (sizeId && saveSize) handleToastOpen("success", "Sticker size saved for this asset");
    } catch (error) {
      if (error.needSize) {
        // Asset has no sticker size -> ask which size to use
        if (sizeOptions.length === 0) {
          try {
            const response = await fetchSizeOptions();
            setSizeOptions(response.data || []);
          } catch (e) {
            setSizeOptions([]);
          }
        }
        setPrintSizeAsk(error.needSize);
      } else {
        console.error("Error generating PDF:", error);
      }
    }
  };

  const handlePrint = () => {
    if (canPrint()) {
      runPrint();
    }
  };

  const handleAssign = () => {
    if (canAssign()) {
      setAssignPopState(true);
    }
  };

  const handleReturn = () => {
    if (canAssign()) {
      setReturnPopState(true);
    }
  };

  const handleStatus = () => {
    if (canViewStatus()) {
      setStatusPopState(true);
    }
  };

  const handleDashboard = () => {
    if (canAccessDashboard()) {
      window.open("/asset-it/file-upload", "_blank");
    }
  };

  const handleImageUpload = (id) => {
    if (canUploadImage()) {
      const url = `/asset-it/image-upload?id=${id}`;
      AssetDetailsAPI.openExternalUrl(url);
    }
  };

  const handleLoginRedirect = () => {
    AssetDetailsAPI.setLoginRedirectUrl(assetId);
    navigate("/login");
  };

  const handleClose = () => {
    navigate("/login");
  };

  const handleManufactureLink = () => {
    const url = AssetDetailsAPI.generateManufactureLink(assetDetails);
    if (url) {
      AssetDetailsAPI.openExternalUrl(url);
    }
  };

  // Add this helper function before the renderAssetInfoTable function
  const calculateDateDifference = (dateString, isWarranty = false) => {
    if (!dateString) return "";

    const targetDate = new Date(dateString);
    const currentDate = new Date();

    // Reset time to avoid time zone issues
    targetDate.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);

    let years, months, daysDiff;

    if (isWarranty) {
      // For warranty: check if expired or remaining
      daysDiff = targetDate.getTime() - currentDate.getTime();
      const isExpired = daysDiff < 0;

      if (isExpired) {
        // Warranty expired - calculate how long ago
        daysDiff = Math.abs(daysDiff);
        years = Math.floor(daysDiff / (365.25 * 24 * 60 * 60 * 1000));
        months = Math.floor(
          (daysDiff % (365.25 * 24 * 60 * 60 * 1000)) /
            (30.44 * 24 * 60 * 60 * 1000)
        );

        let expiredText = "";
        if (years > 0 && months > 0) {
          expiredText = `${years}.${months} years expired`;
        } else if (years > 0) {
          expiredText = `${years} years expired`;
        } else if (months > 0) {
          expiredText = `${months} months expired`;
        } else {
          expiredText = "Recently expired";
        }

        return {
          text: expiredText,
          color: "#d32f2f", // Red color for expired
          isExpired: true,
        };
      } else {
        // Warranty remaining
        years = Math.floor(daysDiff / (365.25 * 24 * 60 * 60 * 1000));
        months = Math.floor(
          (daysDiff % (365.25 * 24 * 60 * 60 * 1000)) /
            (30.44 * 24 * 60 * 60 * 1000)
        );

        let remainingText = "";
        if (years > 0 && months > 0) {
          remainingText = `${years}.${months} years remaining`;
        } else if (years > 0) {
          remainingText = `${years} years remaining`;
        } else if (months > 0) {
          remainingText = `${months} months remaining`;
        } else {
          remainingText = "Expiring soon";
        }

        return {
          text: remainingText,
          color: "#24ae2b", // Green color for remaining
          isExpired: false,
        };
      }
    } else {
      // For purchase date: calculate years since purchase
      daysDiff = currentDate.getTime() - targetDate.getTime();
      years = Math.floor(daysDiff / (365.25 * 24 * 60 * 60 * 1000));
      months = Math.floor(
        (daysDiff % (365.25 * 24 * 60 * 60 * 1000)) /
          (30.44 * 24 * 60 * 60 * 1000)
      );

      let purchaseText = "";
      if (years > 0 && months > 0) {
        purchaseText = `${years}.${months} years of purchase`;
      } else if (years > 0) {
        purchaseText = `${years} years of purchase`;
      } else if (months > 0) {
        purchaseText = `${months} months of purchase`;
      } else {
        purchaseText = "Recently purchased";
      }

      return {
        text: purchaseText,
        color: "#af9007", // Blue color for purchase
        isExpired: false,
      };
    }
  };

  // Updated renderAssetInfoTable function
  const renderAssetInfoTable = () => {
    const assetInfo = assetDetails?.asset_info || {};
    const filteredInfo = Object.entries(assetInfo).filter(
      ([key, value]) =>
        value && value !== "" && value !== null && value !== undefined
    );

    return (
      <TableBody>
        {filteredInfo.map(([key, value]) => {
          let displayValue = value;
          let additionalInfo = null;

          // Check for Purchase Date
          if (key === "Purchase Date" && value) {
            const calculation = calculateDateDifference(value, false);
            additionalInfo = (
              <span style={{ color: calculation.color, fontWeight: "normal" }}>
                {` (${calculation.text})`}
              </span>
            );
          }

          // Check for Warranty Date
          if (key === "Warranty Date" && value) {
            const calculation = calculateDateDifference(value, true);
            additionalInfo = (
              <span style={{ color: calculation.color, fontWeight: "normal" }}>
                {` (${calculation.text})`}
              </span>
            );
          }

          return (
            <TableRow key={key}>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  width: `${isXs ? "51.5%" : "30%"}`,
                  border: "none",
                  paddingY: "0.5rem",
                  verticalAlign: "top",
                }}
              >
                {key}
              </TableCell>
              <TableCell
                sx={{
                  width: `${isXs ? "50%" : "70%"}`,
                  border: "none",
                  paddingY: "0.5rem",
                  verticalAlign: "top",
                }}
              >
                {displayValue}
                {additionalInfo}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  const handleCloseModal = () => {
    setAssignPopState(false);
  };
  const handleAssetCloseModal = () => {
    setStatusPopState(false);
  };
  const handleReturnCloseModal = () => {
    setReturnPopState(false);
  };
  // Retire / Restore: refresh quietly (status, red box, buttons)
  const handleRetireChanged = () => {
    fetchAssetDetails(true);
    AssetDetailsAPI.notifyAssetChanged(assetId); // refresh the asset list in other tabs
  };
  const isRetired = !!assetDetails?.retired_info;
  const handleReturnSuccess = () => {
    fetchAssetDetails(true);   // refresh quietly: employee -> Not assigned, status -> Available
    fetchAssetHistory();   // new "To" date + reason
  };
  const warningState = getWarningState();

  return (
    <>
      {error && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "#fff",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              position: "relative",
              backgroundColor: "#fff",
              borderRadius: "20px",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
              width: "80%",
              maxWidth: "350px",
              textAlign: "center",
            }}
          >
            <button
              onClick={handleClose}
              style={{
                position: "absolute",
                top: "20px",
                right: "10px",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              <CloseIcon style={{ fontSize: "35px" }} />
            </button>

            <WarningIcon
              style={{
                fontSize: "50px",
                color: "red",
                margin: "60px 30px 0",
              }}
            />
            <p
              style={{
                fontSize: "1.1em",
                color: "#333",
                lineHeight: "35px",
                fontWeight: "300",
                padding: "0 10px",
              }}
            >
              Asset not found
            </p>

            <button
              onClick={handleClose}
              style={{
                background: "none",
                border: "none",
                width: "100%",
                fontSize: "20px",
                cursor: "pointer",
                height: "50px",
                backgroundColor: "red",
                color: "#fff",
                borderRadius: "0 0 10px 10px",
                marginTop: "20px",
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
      <Grid
        item
        xs={10}
        sm={10}
        md={10}
        lg={10}
        xl={10}
        sx={{
          margin: `${
            isXs ? "3.5em 0.5em 0.5em 0.5em" : "4em 0.5em 0.5em 0.5em"
          }`,
        }}
      >
        <Card sx={{ padding: `${isXs ? "0.1em 0.5em" : "0.2em 0.9em"}` }}>
          {ackToken && assetDetails && (
            <AckBanner
              token={ackToken}
              assetId={assetId}
              isLoggedIn={localValidToken}
              onConfirmed={() => {
                if (localValidToken) fetchAssetHistory(); // show the new "Received" date
              }}
            />
          )}
          {isRetired && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1.5,
                backgroundColor: "#ffebee",
                border: "1px solid #ef9a9a",
                borderRadius: "8px",
                p: "12px 16px",
                mt: "0.8em",
                mb: "0.5em",
              }}
            >
              <BlockIcon sx={{ color: "#c62828" }} />
              <Box sx={{ flex: 1, minWidth: 220 }}>
                <Typography sx={{ fontWeight: 600, color: "#c62828" }}>
                  {assetDetails.retired_info.type === "Retired"
                    ? "This asset is retired"
                    : `This asset is retired – ${assetDetails.retired_info.type}`}
                </Typography>
                <Typography sx={{ fontSize: "0.85rem", color: "#555" }}>
                  On {formatDateTime(assetDetails.retired_info.at)}
                  {assetDetails.retired_info.by ? ` by ${assetDetails.retired_info.by}` : ""}.
                  Reason: {assetDetails.retired_info.reason}
                </Typography>
              </Box>
              {canAssign() && (
                <Button
                  variant="outlined"
                  color="success"
                  size="small"
                  onClick={() => setRestorePopState(true)}
                >
                  Restore
                </Button>
              )}
            </Box>
          )}
          {warningState.showWarning && (
            <div
              style={{
                display: "flex",
                justifyContent: "start",
                alignItems: "center",
                gap: "0.5em",
                backgroundColor: warningState.backgroundColor,
                padding: ".5em 1em",
                borderRadius: ".5em",
                marginBottom: "0.1em",
                marginTop: `${isXs ? "0.5em" : "0.8em"}`,
              }}
            >
              {warningState.isClickable ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "0.5em",
                    backgroundColor: "#fff8f0",
                    borderRadius: "4px",
                    padding: "8px 12px",
                  }}
                >
                  <WarningIcon
                    sx={{
                      fontSize: 18,
                      color: "orange",
                      mt: "2px",
                      flexShrink: 0, // keeps icon size fixed
                    }}
                  />

                  <Typography
                    sx={{
                      fontWeight: 300,
                      color: warningState.messageColor,
                      fontSize: "0.9em",
                      lineHeight: 1.5,
                    }}
                  >
                    You need to be logged in to access the below Links section.{" "}
                    <button
                      onClick={handleLoginRedirect}
                      style={{
                        border: "none",
                        background: "transparent",
                        color: "#1976d2",
                        cursor: "pointer",
                        padding: 0,
                        margin: 0,
                        fontSize: "0.9em",
                        fontWeight: 400,
                        textDecoration: "underline",
                      }}
                    >
                      Click here to log in.
                    </button>
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "0.4em",
                  }}
                >
                  <WarningIcon
                    sx={{
                      fontSize: 16,
                      color: "orange",
                      mt: "2px", // slight top margin so it aligns with first line
                    }}
                  />
                  <Typography
                    sx={{
                      fontWeight: 300,
                      color: warningState.messageColor,
                      fontSize: "0.85em",
                      lineHeight: 1.4, // makes multiline text look clean
                    }}
                  >
                    {warningState.message}
                  </Typography>
                </Box>
              )}
            </div>
          )}

          <Grid
            container
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {assetDetails && (
              <OrderBox>
                <OrderBoxText>
                  <span
                    style={{
                      position: "absolute",
                      left: "50%",
                      transform: "translateX(-50%)",
                    }}
                  >
                    General Info
                  </span>
                  <IconButton
                    onClick={() => setGeneralInfoExpanded(!generalInfoExpanded)}
                    sx={{
                      position: "absolute",
                      right: "0.5em",
                      color: "white",
                      "&:hover": { backgroundColor: "rgba(255, 255, 255, 0)" },
                    }}
                  >
                    {generalInfoExpanded ? (
                      <KeyboardArrowUpIcon />
                    ) : (
                      <KeyboardArrowDownIcon />
                    )}
                  </IconButton>
                </OrderBoxText>
                {generalInfoExpanded ? (
                  <TableContainer>
                    <Table size="small">
                      <TableBody sx={{ marginY: "rem" }}>
                        {Object.entries(assetDetails.general_info || {}).map(
                          ([key, value]) => (
                            <TableRow key={key}>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  width: `${isXs ? "51.5%" : "30%"}`,
                                  border: "none",
                                  paddingY: "0.5rem",
                                  verticalAlign: "top",
                                }}
                              >
                                {key}
                              </TableCell>
                              <TableCell
                                sx={{
                                  border: "none",
                                  width: `${isXs ? "50%" : "70%"}`,
                                  paddingY: "0.5rem",
                                  verticalAlign: "top",
                                }}
                              >
                                {key === "Employee Name" && !value ? (
                                  <span style={{ color: "#c62828", fontWeight: 600 }}>
                                    Not assigned
                                  </span>
                                ) : (
                                  value
                                )}
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <></>
                )}
              </OrderBox>
            )}
          </Grid>

          {assetDetails && (
            <OrderBox sx={{ marginTop: "0.1em" }}>
              <OrderBoxText>
                <span
                  style={{
                    position: "absolute",
                    left: "50%",
                    transform: "translateX(-50%)",
                  }}
                >
                  Asset Info
                </span>
                <IconButton
                  onClick={() => setAssetInfoExpanded(!assetInfoExpanded)}
                  sx={{
                    position: "absolute",
                    right: "0.5em",
                    color: "white",
                    "&:hover": { backgroundColor: "rgba(255, 255, 255, 0)" },
                  }}
                >
                  {assetInfoExpanded ? (
                    <KeyboardArrowUpIcon />
                  ) : (
                    <KeyboardArrowDownIcon />
                  )}
                </IconButton>
              </OrderBoxText>
              {assetInfoExpanded ? (
                <TableContainer>
                  <Table size="small">{renderAssetInfoTable()}</Table>
                </TableContainer>
              ) : (
                ""
              )}
            </OrderBox>
          )}

          {assetDetails && localValidToken && (
            <OrderBox sx={{ marginTop: "0.1em" }}>
              <OrderBoxText>
                <span
                  style={{
                    position: "absolute",
                    left: "50%",
                    transform: "translateX(-50%)",
                  }}
                >
                  Assignment History
                </span>
                <IconButton
                  onClick={() => setHistoryExpanded(!historyExpanded)}
                  sx={{
                    position: "absolute",
                    right: "0.5em",
                    color: "white",
                    "&:hover": { backgroundColor: "rgba(255, 255, 255, 0)" },
                  }}
                >
                  {historyExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
              </OrderBoxText>

              {historyExpanded &&
                (historyLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : assetHistory.length === 0 ? (
                  <Typography sx={{ p: 2, textAlign: "center", color: "#666", fontSize: "0.9em" }}>
                    No assignment history
                  </Typography>
                ) : (
                  <TableContainer sx={{ overflowX: "auto" }}>
                    <Table size="small" sx={{ minWidth: 650 }}>
                      <TableHead>
                        <TableRow>
                          {["Employee", "Code", "From", "Received?", "To", "Return reason", "Condition", "Assigned by"].map(
                            (h) => (
                              <TableCell
                                key={h}
                                sx={{
                                  fontWeight: "bold",
                                  backgroundColor: "#f2f8f2",
                                  color: "#2b6b2f",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {h}
                              </TableCell>
                            )
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {assetHistory.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell
                              sx={{
                                fontWeight: row.is_current ? "bold" : "normal",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.employee_name || "-"}
                            </TableCell>
                            <TableCell>{row.employee_code || "-"}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              {formatDateTime(row.assigned_at)}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              {row.acknowledged_at ? (
                                <span
                                  title={`Confirmed on ${formatDateTime(row.acknowledged_at)}`}
                                  style={{
                                    backgroundColor: "#e8f5e9",
                                    color: "#2e7d32",
                                    fontSize: "0.75em",
                                    padding: "2px 10px",
                                    borderRadius: "10px",
                                    fontWeight: 600,
                                  }}
                                >
                                  ✓ {formatDateTime(row.acknowledged_at).split(",")[0]}
                                </span>
                              ) : row.ack_requested ? (
                                <span
                                  style={{
                                    backgroundColor: row.is_current ? "#fff3e0" : "#eeeeee",
                                    color: row.is_current ? "#e65100" : "#757575",
                                    fontSize: "0.75em",
                                    padding: "2px 10px",
                                    borderRadius: "10px",
                                    fontWeight: 600,
                                  }}
                                >
                                  {row.is_current ? "Pending" : "Not confirmed"}
                                </span>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              {row.is_current ? (
                                <span
                                  style={{
                                    backgroundColor: "#3CB043",
                                    color: "#fff",
                                    fontSize: "0.75em",
                                    padding: "2px 10px",
                                    borderRadius: "10px",
                                    fontWeight: 600,
                                  }}
                                >
                                  Current
                                </span>
                              ) : (
                                <>
                                  {formatDateTime(row.returned_at)}
                                  {row.return_recorded_by && (
                                    <div style={{ fontSize: "0.75em", color: "#888" }}>
                                      recorded by {row.return_recorded_by}
                                    </div>
                                  )}
                                </>
                              )}
                            </TableCell>
                            <TableCell>{row.return_reason || "-"}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              {row.return_condition ? (
                                <span
                                  style={{
                                    ...({
                                      Good: { backgroundColor: "#e8f5e9", color: "#2e7d32" },
                                      Damaged: { backgroundColor: "#ffebee", color: "#c62828" },
                                      "Missing parts": { backgroundColor: "#fff3e0", color: "#e65100" },
                                    }[row.return_condition] || { backgroundColor: "#eee", color: "#555" }),
                                    fontSize: "0.75em",
                                    padding: "2px 10px",
                                    borderRadius: "10px",
                                    fontWeight: 600,
                                  }}
                                >
                                  {row.return_condition}
                                </span>
                              ) : (
                                "-"
                              )}
                              {row.return_notes && (
                                <div
                                  style={{
                                    fontSize: "0.75em",
                                    color: "#666",
                                    whiteSpace: "normal",
                                    maxWidth: 220,
                                    marginTop: 4,
                                  }}
                                >
                                  {row.return_notes}
                                </div>
                              )}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              {row.assigned_by || "-"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ))}
            </OrderBox>
          )}

          {assetDetails && localValidToken && (
            <RepairSection
              assetId={assetId}
              canEdit={canAssign()}
              isRetired={isRetired}
              onChanged={handleRetireChanged}
            />
          )}

          {assetDetails && localValidToken && <EditHistorySection assetId={assetId} />}

          {assetDetails && (
            <OrderBox sx={{ marginTop: "1.2em" }}>
              <OrderBoxText>Manufacture Info</OrderBoxText>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <TableCell sx={{ paddingY: "2rem" }}>
                        <Button
                          variant="text"
                          onClick={handleManufactureLink}
                          sx={{
                            textTransform: "none",
                            padding: 0,
                            minWidth: "auto",
                            color: "#001aff",
                            backgroundColor: "transparent",
                            textDecoration: "underline",
                            "&:hover": {
                              backgroundColor: "transparent",
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {assetDetails?.asset_info?.Brand &&
                          assetDetails?.asset_info?.["Sub Series"]
                            ? `${assetDetails.asset_info.Brand} - ${assetDetails.asset_info["Laptop/Desktop Serial No"]}`
                            : `${assetDetails.asset_info.Brand} ${assetDetails.asset_info["Laptop/Desktop Serial No"]}`}
                        </Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </OrderBox>
          )}

          <Grid item sx={{ marginTop: "1.2em" }}>
            {assetDetails && (
              <OrderBox>
                <OrderBoxText>Links</OrderBoxText>
                <OrderBoxBtn>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: { xs: 2, sm: 3 },
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {/* Image Preview Button - Always available */}
                    {assetDetails?.asset_images?.length > 0 && (
                      <CButton
                        cId="asset_image_btn_preview"
                        cTooltipText="Image View"
                        cSrc={<PreviewIcon />}
                        cOnClick={() => setOpenCarousel(true)}
                        cIsMain={true}
                      />
                    )}

                    {/* Image Upload Button - Only if has permission */}
                    {assetDetails?.asset_images?.length === 0 && (
                      <CButton
                        cId="asset_image_btn_upload"
                        cTooltipText="Image Upload"
                        cSrc={<CameraAltIcon />}
                        cOnClick={() =>
                          handleImageUpload(
                            assetDetails.general_info["Asset ID"]
                          )
                        }
                        cDisabled={
                          warningState.disableButtons || !canUploadImage()
                        }
                        cIsMain={true}
                      />
                    )}

                    {/* Print Button */}
                    <CButton
                      cId="asset_sticker"
                      cTooltipText="Asset Sticker"
                      cSrc={<LocalPrintshopIcon />}
                      cOnClick={handlePrint}
                      cDisabled={warningState.disableButtons || !canPrint()}
                      cIsMain={true}
                    />

                    {/* Assign Button */}
                    <CButton
                      cId="asset_assign"
                      cTooltipText="Assign Asset"
                      cSrc={<AssignmentIcon />}
                      cOnClick={handleAssign}
                      cDisabled={warningState.disableButtons || !canAssign() || isRetired}
                      cIsMain={true}
                    />

                    {/* Return Button: only when someone has the asset */}
                    <CButton
                      cId="asset_return"
                      cTooltipText="Return Asset"
                      cSrc={<AssignmentReturnIcon />}
                      cOnClick={handleReturn}
                      cDisabled={
                        warningState.disableButtons ||
                        !canAssign() ||
                        !assetDetails?.general_info?.["Employee Name"]
                      }
                      cIsMain={true}
                    />

                    {/* Retire Button: only when nobody has the asset */}
                    <CButton
                      cId="asset_retire"
                      cTooltipText="Retire / Lost / Disposed"
                      cSrc={<BlockIcon />}
                      cOnClick={() => canAssign() && setRetirePopState(true)}
                      cDisabled={
                        warningState.disableButtons ||
                        !canAssign() ||
                        isRetired ||
                        !!assetDetails?.general_info?.["Employee Name"]
                      }
                      cIsMain={true}
                    />

                    {/* Status Button */}
                    <CButton
                      cId="asset_status"
                      cTooltipText="Asset Status"
                      cSrc={<BarChartIcon />}
                      cOnClick={handleStatus}
                      cIsMain={true}
                      cDisabled={
                        warningState.disableButtons || !canViewStatus() || isRetired
                      }
                    />

                    {/* Dashboard Button */}
                    <CButton
                      cId="asset_dashboard"
                      cTooltipText="Dashboard"
                      cSrc={<DashboardIcon />}
                      cOnClick={handleDashboard}
                      cIsMain={true}
                      cDisabled={
                        warningState.disableButtons || !canAccessDashboard()
                      }
                    />
                  </Box>
                </OrderBoxBtn>
              </OrderBox>
            )}
          </Grid>
        </Card>
      </Grid>

      {/* Image Carousel */}
      {openCarousel && (
        <CCarousel
          cOpen={openCarousel}
          images={assetDetails?.asset_images}
          cClose={() => setOpenCarousel(false)}
        />
      )}
      {
        <AssignModal
          open={assignPopState}
          onClose={handleCloseModal}
          cUserDataArr={{
            asset_id: assetDetails?.general_info?.["Asset ID"],
            name: assetDetails?.general_info?.["Employee Name"],
            emp_code: assetDetails?.general_info?.["Employee Code"],
          }}
        />
      }
      {
        <ReturnModal
          open={returnPopState}
          onClose={handleReturnCloseModal}
          onSuccess={handleReturnSuccess}
          cAssetData={{
            asset_id: assetDetails?.general_info?.["Asset ID"],
            employee_name: assetDetails?.general_info?.["Employee Name"],
            employee_code: assetDetails?.general_info?.["Employee Code"],
          }}
        />
      }
      <RetireModal
        open={retirePopState}
        onClose={() => setRetirePopState(false)}
        onSuccess={handleRetireChanged}
        cAssetData={{
          asset_id: assetDetails?.general_info?.["Asset ID"],
          category: assetDetails?.general_info?.Category,
        }}
      />
      <PrintSizeModal
        open={!!printSizeAsk}
        missingAssets={printSizeAsk?.missing_assets || []}
        missingCount={printSizeAsk?.missing_count || 0}
        sizeOptions={sizeOptions}
        onCancel={() => setPrintSizeAsk(null)}
        onConfirm={(sizeId, saveSize) => {
          setPrintSizeAsk(null);
          runPrint(sizeId, saveSize);
        }}
      />
      <RestoreModal
        open={restorePopState}
        onClose={() => setRestorePopState(false)}
        onSuccess={handleRetireChanged}
        cAssetData={{ asset_id: assetDetails?.general_info?.["Asset ID"] }}
      />
      {
        <AssetUpdateModal
          open={statusPopState}
          onClose={handleAssetCloseModal}
          cAssetDataArr={{
            asset_id: assetDetails?.general_info?.["Asset ID"],
            warranty_status: assetDetails?.asset_info?.["Warranty Status"],
            warranty_date: assetDetails?.asset_info?.["Warranty Date"],
            asset_status: assetDetails?.general_info?.["Status"],
            os_system: assetDetails?.os_system,
            os: assetDetails?.asset_info?.["Operating System"],
            ...(() => {
              const {
                ["Warranty Date"]: _,
                ["Warranty Status"]: __,["Operating System"]: ___,
                ...rest
              } = assetDetails?.asset_info || {};
              return rest;
            })(),
          }}
        />
      }
    </>
  );
}

export default AssetDetailsView;